import './StaticParties.css';
import html from './StaticParties.html';
import { connectLobby } from '../../api/party';
import { listStaticParties } from '../../api/staticParty';
import { escapeHtml } from '../../tiles';
import { SOURCE_LABELS } from '../staticParty/StaticParty';
import { LOADING_HINT } from '../../loading';
import type { StaticPartySummaryDto } from '../../types';

/**
 * Route `/static` - the "Lounge parties" tab: every static
 * watch party, kept apart from the home page's classic live parties
 * (whose lobby list leaves static sessions out - see Home.ts).
 *
 * A REST list, re-fetched whenever the set of live static-party sessions
 * in the lobby changes (a session starting or ending is what moves a
 * card's "séance en cours"/progress state). Not on every lobby snapshot:
 * those also fire on every pause/join, and each list fetch reads Emby
 * once per static party (see server/src/index.ts's syncStaticSource).
 */
export function renderStaticParties(root: HTMLElement): () => void {
  root.innerHTML = html;
  const grid = root.querySelector('#staticGrid') as HTMLElement;
  grid.innerHTML = LOADING_HINT;

  let sessionsKey: string | null = null;
  const lobby = connectLobby((parties) => {
    const key = parties.map((p) => p.staticPartyId).filter(Boolean).sort().join(',');
    if (key === sessionsKey) return;
    sessionsKey = key;
    load(grid);
  });

  // Same leak-prevention contract as Home.ts: the router calls this before
  // rendering the next view.
  return function cleanup() {
    lobby.disconnect();
  };
}

/** Fetches and renders every static party into `grid`. */
function load(grid: HTMLElement): void {
  listStaticParties()
    .then((parties) => {
      if (!grid.isConnected) return; // navigated away mid-flight
      render(grid, parties);
    })
    .catch((err: unknown) => {
      console.error('[staticParties] list failed to load', err);
      if (grid.isConnected) grid.innerHTML = `<p class="error">${escapeHtml((err as Error).message)}</p>`;
    });
}

/** One card per static party: progress, next item, the viewer's missed
 *  count, and whether a session is running right now. */
function render(grid: HTMLElement, parties: StaticPartySummaryDto[]): void {
  if (!parties.length) {
    grid.innerHTML = '<p class="hint">Aucune lounge party pour le moment.</p>';
    return;
  }
  grid.innerHTML = parties.map((p) => {
    const percent = p.itemCount ? Math.round((p.groupWatchedCount / p.itemCount) * 100) : 0;
    return `
      <a class="staticCard" href="#/static/${escapeHtml(p.id)}">
        <img src="${escapeHtml(p.imageUrl)}" alt="" loading="lazy" onerror="this.classList.add('noImage')" />
        <div class="staticCardInfo">
          <strong>${escapeHtml(p.name)}</strong>
          <span class="hint">${SOURCE_LABELS[p.sourceType]} · ${p.groupWatchedCount}/${p.itemCount} vus · ${p.memberCount} membre${p.memberCount > 1 ? 's' : ''}</span>
          <div class="staticCardBar"><div style="width:${percent}%"></div></div>
          ${p.livePartyId
            ? '<span class="staticCardLive">● Séance en cours</span>'
            : `<span class="staticCardNext">${p.nextItemName ? `Prochain : ${escapeHtml(p.nextItemName)}` : 'Le groupe a tout vu.'}</span>`}
        </div>
        ${p.missedCount ? `<span class="staticCardMissed" title="Éléments manqués">${p.missedCount} manqué${p.missedCount > 1 ? 's' : ''}</span>` : ''}
      </a>`;
  }).join('');
}
